import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// eslint-disable-next-line
import { pb, getCurrentUser } from "../services/pocketbase";

// Privacy Policy Component
const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [roleAssigned, setRoleAssigned] = useState(false);
  // eslint-disable-next-line
  const [showNotification, setShowNotification] = useState(false);
  const user = getCurrentUser();
  const isLoggedIn = !!user;
  const privacyPolicyRef = useRef(null);

  // Email obfuscation function
  const ObfuscatedEmail = () => {
    // This creates a reference to store the email parts
    const [emailParts] = React.useState({
      prefix: "contact",
      symbol: "@",
      domain: "togx.org",
    });

    // Function to assemble and copy email to clipboard
    const handleEmailClick = () => {
      const email = `${emailParts.prefix}${emailParts.symbol}${emailParts.domain}`;
      navigator.clipboard.writeText(email);
      alert("Email address copied to clipboard!");
    };

    return (
      <Box
        component="span"
        onClick={handleEmailClick}
        sx={{
          cursor: "pointer",
          textDecoration: "underline",
          "&:hover": {
            color: "#c4d4e0",
          },
        }}
      >
        {/* Render email parts separately to avoid simple scraping */}
        <span>{emailParts.prefix}</span>
        <span>{emailParts.symbol}</span>
        <span>{emailParts.domain}</span> (click to copy)
      </Box>
    );
  };

  // Add scroll tracking effect
  useEffect(() => {
    if (!isLoggedIn || roleAssigned) return;

    const handleScroll = () => {
      if (!privacyPolicyRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        privacyPolicyRef.current;
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (scrolledToBottom) {
        // Assign Discord role
        assignDiscordRole();
      }
    };

    const container = privacyPolicyRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    }; // eslint-disable-next-line
  }, [isLoggedIn, roleAssigned]);

  // Function to assign Discord role
  const assignDiscordRole = async () => {
    if (!isLoggedIn || roleAssigned || !user.discord_id) return;

    try {
      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: "1372557799053328456",
        }),
      });

      if (response.ok) {
        setRoleAssigned(true);
        setShowNotification(true);

        // Trigger the notification
        if (window.showNotification) {
          window.showNotification(
            "Thank you for reading our Privacy Policy! You've been granted the Privacy Reader role in our Discord server."
          );
        }

        setTimeout(() => setShowNotification(false), 5000);
      } else {
        console.error("Failed to assign Discord role:", await response.text());
      }
    } catch (error) {
      console.error("Error assigning Discord role:", error);
    }
  };

  return (
    <Box>
      <Typography
        variant="h2"
        component="h1"
        align="center"
        sx={{
          color: "white",
          fontWeight: "bold",
          mb: 4,
        }}
      >
        PRIVACY POLICY
      </Typography>
      <Box
        ref={privacyPolicyRef}
        sx={{
          whiteSpace: "pre-wrap",
          backgroundColor: "rgba(40, 44, 52, 0.99)",
          borderRadius: 2,
          p: 4,
          maxHeight: "70vh",
          overflowY: "auto",
          "& h1": {
            fontSize: "1.8rem",
            fontWeight: "bold",
            mb: 2,
          },
          "& h2": {
            fontSize: "1.4rem",
            fontWeight: "bold",
            mb: 1.5,
            mt: 3,
          },
          "& h5": {
            fontSize: "1.2rem",
            fontWeight: "bold",
            mb: 1,
            mt: 3,
          },
          "& p": {
            mb: 2,
          },
          "& table": {
            width: "100%",
            borderCollapse: "collapse",
            mb: 3,
          },
          "& th, & td": {
            border: "1px solid rgba(255, 255, 255, 0.2)",
            padding: "8px 12px",
            textAlign: "left",
          },
          "& th": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            fontWeight: "bold",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/policies")}
          >
            Back
          </Button>
        </Box>

        <Typography paragraph sx={{ fontWeight: "bold" }}>
          Effective Date: 12-May-2025
        </Typography>

        <Typography paragraph>
          The Order of Gaming ("TOG") respects your privacy and will handle your
          data with care. This Privacy Policy explains how we collect, use,
          store, protect and share your personal information when you use or
          participate in TOG's services, including our website, Discord server,
          tournaments, forums and affiliated platforms ("Services").
        </Typography>

        <Typography paragraph>
          We are based in the U.S. but comply with applicable data protection
          laws including the EU's General Data Protection Regulation (GDPR) and
          the UK's Data Protection Act 2018 to protect our global user base.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          1. Key Principles
        </Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • We care about your privacy. Our goal is to create safe and fun
          online spaces.
          <br />
          • We don't sell your data. Our business model is based on
          subscriptions, sponsorships or paid products, not data trading.
          <br />
          • Minimal data collection. We only collect what's necessary to provide
          Services and meet legal obligations.
          <br />• You're in control. Manage your data through account settings
          or contact us.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          2. Information We Collect
        </Typography>
        <Typography variant="h6" component="h4" sx={{ mt: 2 }}>
          2.1 Information You Provide
        </Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • Account Details: Username, email address, avatar, Discord ID and
          (optional) social handles.
          <br />
          • Activity Data: Participation logs, game statistics, submitted
          content (e.g. clips, forum posts).
          <br />• Communication Data: Messages sent to us or posted on public
          forums.
        </Typography>

        <Typography variant="h6" component="h4" sx={{ mt: 2 }}>
          2.2 Information Collected Automatically
        </Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • Technical Data: IP address, device/browser type (Third-Party
          Analytics, e.g. Google Analytics).
          <br />• Platform Data: User ID, display name, roles in our Discord
          server.
        </Typography>

        <Typography paragraph>
          Note: We do not collect sensitive data (e.g. real name, address,
          payment info) unless required (e.g. for prize fulfillment) and with
          your consent.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          3. How We Use Your Information
        </Typography>
        <Typography paragraph>We process data to:</Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • Provide, maintain and improve our Services.
          <br />
          • Facilitate competitions, leaderboards and community features.
          <br />
          • Send updates, security alerts or administrative messages.
          <br />
          • Ensure safety, enforce our Code of Conduct and prevent harmful
          content.
          <br />• Comply with legal obligations (e.g. Discord policies, law
          enforcement requests).
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          4. Data Sharing and Disclosure
        </Typography>
        <Typography paragraph>
          We may share your information in these cases:
        </Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • Legal Requirements: Valid requests by public authorities.
          <br />
          • Service Providers: Third-party tools (e.g. analytics, Discord bots)
          with strict privacy compliance.
          <br />
          • Safety: To protect users or others.
          <br />• Aggregated Data: For analytics or marketing (e.g. participant
          numbers).
        </Typography>
        <Typography paragraph>We don't sell your data.</Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          5. Data Retention
        </Typography>
        <Typography paragraph>
          We keep personal data only as long as needed for purpose, legal
          requirements or operational needs. Inactive or deleted accounts are
          purged after a reasonable time unless retention is required by law.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          6. Data Security
        </Typography>
        <Typography paragraph>We do our best to protect your data:</Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • Encryption: Data is encrypted in transit and at rest.
          <br />
          • Access Controls: Limited to authorized personnel.
          <br />• Monitoring: Regular system checks for vulnerabilities.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          7. International Data Transfers
        </Typography>
        <Typography paragraph>
          As a US-based company, TOG may transfer, store and process your
          personal data in locations outside your country of residence,
          including the United States. These transfers are necessary to provide
          our Services and operate globally.
        </Typography>

        <Typography variant="h6" component="h4" sx={{ mt: 2 }}>
          7.1 Safeguards for International Data Transfers
        </Typography>
        <Typography paragraph>
          To comply with global data protection laws (including GDPR and UK Data
          Protection Act 2018), we use:
        </Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • Standard Contractual Clauses (SCCs): For EU/UK transfers, we use
          EU-approved SCCs (and UK Addendum where applicable).
          <br />
          • EU-U.S. Data Privacy Framework (DPF): TOG complies with the EU-U.S.
          DPF and UK Extension, so GDPR-level protections.
          <br />• Additional Protections: Data minimization, encryption and
          third-party audits.
        </Typography>

        <Typography variant="h6" component="h4" sx={{ mt: 2 }}>
          7.2 Your Rights Regarding Transfers
        </Typography>
        <Typography paragraph>You can:</Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          • Request details on safeguards.
          <br />
          • Object to transfers where legally permitted.
          <br />
          • Email us at <ObfuscatedEmail /> for more info.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          8. Your Data Protection Rights
        </Typography>
        <Typography paragraph>
          Under GDPR, UK DPA and other laws, you have the right to:
        </Typography>

        <table>
          <thead>
            <tr>
              <th>Right</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Access</td>
              <td>Get a copy of your personal data.</td>
            </tr>
            <tr>
              <td>Rectification</td>
              <td>Correct inaccurate data.</td>
            </tr>
            <tr>
              <td>Erasure</td>
              <td>Delete where no legal basis exists.</td>
            </tr>
            <tr>
              <td>Restriction</td>
              <td>Limit processing (e.g., while disputing accuracy).</td>
            </tr>
            <tr>
              <td>Portability</td>
              <td>Get your data in a machine-readable format.</td>
            </tr>
            <tr>
              <td>Objection</td>
              <td>Object to processing based on legitimate interests.</td>
            </tr>
            <tr>
              <td>Withdraw Consent</td>
              <td>Revoke consent (where applicable).</td>
            </tr>
            <tr>
              <td>Lodge a Complaint</td>
              <td>File a complaint with a data protection authority.</td>
            </tr>
          </tbody>
        </table>

        <Typography variant="h6" component="h4" sx={{ mt: 2 }}>
          8.1 How to Exercise Your Rights
        </Typography>
        <Typography component="div">
          1. Submit a Request:
          <br />
          <Box sx={{ pl: 2 }}>
            o Email: <ObfuscatedEmail />
            <br />
            o Discord: https://discord.gg/togx
            <br />o Website: https://www.togx.org
          </Box>
          <br />
          2. Verification: We may ask for proof of identity for security.
          <br />
          3. Response Time: 30 days (or sooner if required by law).
        </Typography>
        <Typography paragraph>
          Note: No fee unless manifestly unfounded.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          9. Children's Privacy
        </Typography>
        <Typography paragraph>
          Our Services are not intended for users under 16. If we collect data
          from a minor, we will delete it immediately.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          10. Policy Updates
        </Typography>
        <Typography paragraph>
          Major changes will be announced on official channels. Continued use of
          Services means you accept changes.
        </Typography>

        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: "bold", mt: 2 }}
        >
          Contact:
        </Typography>
        <Typography paragraph>
          Email <ObfuscatedEmail /> or https://www.togx.org
        </Typography>
      </Box>
      <Notification />
    </Box>
  );
};

// Cookie Policy Component
const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Typography
        variant="h2"
        component="h1"
        align="center"
        sx={{
          color: "white",
          fontWeight: "bold",
          mb: 4,
        }}
      >
        COOKIE POLICY
      </Typography>
      <Box
        sx={{
          whiteSpace: "pre-wrap",
          backgroundColor: "rgba(40, 44, 52, 0.99)",
          borderRadius: 2,
          p: 4,
          "& h1": {
            fontSize: "1.8rem",
            fontWeight: "bold",
            mb: 2,
          },
          "& h2": {
            fontSize: "1.4rem",
            fontWeight: "bold",
            mb: 1.5,
            mt: 3,
          },
          "& h5": {
            fontSize: "1.2rem",
            fontWeight: "bold",
            mb: 1,
            mt: 3,
          },
          "& p": {
            mb: 2,
          },
          "& a": {
            color: "#c4d4e0",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/policies")}
          >
            Back
          </Button>
        </Box>

        <Typography paragraph>
          Effective Date: 12-May-2025
          <br />
          Last Updated: 12-May-2025
        </Typography>

        <Typography variant="h5" component="h3">
          What are cookies?
        </Typography>
        <Typography paragraph>
          This Cookie Policy explains what cookies are and how we use them, the
          types of cookies we use i.e, the information we collect using cookies
          and how that information is used, and how to manage the cookie
          settings.
        </Typography>
        <Typography paragraph>
          Cookies are small text files that are used to store small pieces of
          information. They are stored on your device when the website is loaded
          on your browser. These cookies help us make the website function
          properly, make it more secure, provide better user experience, and
          understand how the website performs and to analyze what works and
          where it needs improvement.
        </Typography>

        <Typography variant="h5" component="h3">
          How do we use cookies?
        </Typography>
        <Typography paragraph>
          As most of the online services, our website uses first-party and
          third-party cookies for several purposes. First-party cookies are
          mostly necessary for the website to function the right way, and they
          do not collect any of your personally identifiable data.
        </Typography>
        <Typography paragraph>
          The third-party cookies used on our website are mainly for
          understanding how the website performs, how you interact with our
          website, keeping our services secure, providing advertisements that
          are relevant to you, and all in all providing you with a better and
          improved user experience and help speed up your future interactions
          with our website.
        </Typography>

        <Typography variant="h5" component="h3">
          Types of Cookies we use:
        </Typography>
        <div className="cky-audit-table-element"></div>

        <Typography paragraph>
          You can change your cookie preferences any time by clicking the blue
          button on the bottom left of your screen. This will let you revisit
          the cookie consent banner and change your preferences or withdraw your
          consent right away.
        </Typography>
        <Typography paragraph>
          In addition to this, different browsers provide different methods to
          block and delete cookies used by websites. You can change the settings
          of your browser to block/delete the cookies. Listed below are the
          links to the support documents on how to manage and delete cookies
          from the major web browsers.
        </Typography>

        <Typography paragraph>
          Chrome:{" "}
          <Link
            href="https://support.google.com/accounts/answer/32050"
            target="_blank"
          >
            https://support.google.com/accounts/answer/32050
          </Link>
        </Typography>
        <Typography paragraph>
          Safari:{" "}
          <Link
            href="https://support.apple.com/en-in/guide/safari/sfri11471/mac"
            target="_blank"
          >
            https://support.apple.com/en-in/guide/safari/sfri11471/mac
          </Link>
        </Typography>
        <Typography paragraph>
          Firefox:{" "}
          <Link
            href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?redirectslug=delete-cookies-remove-info-websites-stored&redirectlocale=en-US"
            target="_blank"
          >
            https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox
          </Link>
        </Typography>
        <Typography paragraph>
          Internet Explorer:{" "}
          <Link
            href="https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-bca9446f-d873-78de-77ba-d42645fa52fc"
            target="_blank"
          >
            https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-bca9446f-d873-78de-77ba-d42645fa52fc
          </Link>
        </Typography>
        <Typography paragraph>
          If you are using any other web browser, please visit your browser's
          official support documents.
        </Typography>
      </Box>
      <Notification />
    </Box>
  );
};

// Terms of Service Component
const TermsOfService = () => {
  const navigate = useNavigate();
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const user = getCurrentUser();
  const isLoggedIn = !!user;
  const termsRef = useRef(null);

  // Email obfuscation function
  const ObfuscatedEmail = () => {
    // This creates a reference to store the email parts
    const [emailParts] = React.useState({
      prefix: "contact",
      symbol: "@",
      domain: "togx.org",
    });

    // Function to assemble and copy email to clipboard
    const handleEmailClick = () => {
      const email = `${emailParts.prefix}${emailParts.symbol}${emailParts.domain}`;
      navigator.clipboard.writeText(email);
      alert("Email address copied to clipboard!");
    };

    return (
      <Box
        component="span"
        onClick={handleEmailClick}
        sx={{
          cursor: "pointer",
          textDecoration: "underline",
          "&:hover": {
            color: "#c4d4e0",
          },
        }}
      >
        {/* Render email parts separately to avoid simple scraping */}
        <span>{emailParts.prefix}</span>
        <span>{emailParts.symbol}</span>
        <span>{emailParts.domain}</span> (click to copy)
      </Box>
    );
  };

  // Function to assign Discord role
  const assignDiscordRole = async () => {
    if (!isLoggedIn || roleAssigned) return;

    try {
      // Call the API to assign the Discord role
      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: "1372557732481335316",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setRoleAssigned(true);
        setShowNotification(true);

        // Trigger the notification
        if (window.showNotification) {
          window.showNotification(
            "Discord role 'Terms Accepted' has been assigned to your account!"
          );
        }
      } else {
        console.error("Failed to assign role:", data.error);
      }
    } catch (error) {
      console.error("Error assigning role:", error);
    }
  };

  // Handle scroll event to detect when user reaches the bottom
  const handleScroll = () => {
    if (!termsRef.current || roleAssigned || !isLoggedIn) return;

    const { scrollTop, scrollHeight, clientHeight } = termsRef.current;

    // Check if user has scrolled to the bottom (with a small threshold)
    if (scrollHeight - scrollTop - clientHeight < 30) {
      assignDiscordRole();
    }
  };

  // Function to show notification when role is assigned
  const showRoleAssignedNotification = () => {
    if (showNotification) {
      window.showNotification(
        "Discord role successfully assigned! Thank you for accepting our Terms of Service."
      );
    }
  };

  // Show notification when showNotification state changes
  useEffect(() => {
    if (showNotification) {
      showRoleAssignedNotification();
    } // eslint-disable-next-line
  }, [showNotification]);

  return (
    <Box>
      <Typography
        variant="h2"
        component="h1"
        align="center"
        sx={{
          color: "white",
          fontWeight: "bold",
          mb: 4,
        }}
      >
        TERMS OF SERVICE
      </Typography>
      <Box
        ref={termsRef}
        onScroll={handleScroll}
        sx={{
          whiteSpace: "pre-wrap",
          backgroundColor: "rgba(40, 44, 52, 0.99)",
          borderRadius: 2,
          p: 4,
          maxHeight: "70vh",
          overflowY: "auto",
          "& h1": {
            fontSize: "1.8rem",
            fontWeight: "bold",
            mb: 2,
          },
          "& h2": {
            fontSize: "1.4rem",
            fontWeight: "bold",
            mb: 1.5,
            mt: 3,
          },
          "& h5": {
            fontSize: "1.2rem",
            fontWeight: "bold",
            mb: 1,
            mt: 3,
          },
          "& p": {
            mb: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/policies")}
          >
            Back
          </Button>
        </Box>

        <Typography paragraph>Effective Date: 12-May-2025</Typography>

        <Typography variant="h5" component="h3">
          1. Acceptance of Terms
        </Typography>
        <Typography paragraph>
          By using any part of The Order of Gaming ("TOG") services—including
          but not limited to our website, Discord server, game-related
          platforms, and affiliated services (collectively, the "Services")—you
          agree to be bound by these Terms of Service ("Terms"). If you don't
          agree to these Terms, don't use the Services.
        </Typography>
        <Typography paragraph>
          People who read and understand these Terms are highly valued by TOG.
          Apply wisely; leadership may take note.
        </Typography>
        <Typography paragraph>
          If you are under 16 years of age, you may not participate without
          explicit approval from TOG leadership. Use of the Services is void
          where prohibited.
        </Typography>

        <Typography variant="h5" component="h3">
          2. Eligibility and User Responsibilities
        </Typography>
        <Typography paragraph>You represent and warrant that:</Typography>
        <Typography component="div" sx={{ pl: 3, mb: 2 }}>
          • You are at least 16 years old or have received express written
          approval from the TOG Board.
          <br />
          • You have the legal capacity to enter into this agreement under
          applicable laws in your country of residence.
          <br />
          • You will use the Services only for lawful purposes and in compliance
          with all applicable laws and regulations.
          <br />• You will provide accurate and current information when
          registering or communicating with TOG.
        </Typography>

        <Typography variant="h5" component="h3">
          3. Code of Conduct and Privacy Policy
        </Typography>
        <Typography paragraph>
          Your use of TOG's Services is also governed by TOG's Code of Conduct
          and Privacy Policy which are incorporated by reference and form part
          of our user agreement.
        </Typography>
        <Typography component="div" sx={{ pl: 3, mb: 2 }}>
          • The Code of Conduct explains behavioural expectations, community
          standards, internal structure and disciplinary procedures.
          <br />• The Privacy Policy explains how TOG collects, uses and
          protects your personal data and your rights to it.
        </Typography>
        <Typography paragraph>
          By joining TOG, you agree to both documents. Violations may result in
          corrective or disciplinary action up to and including termination of
          access to TOG Services.
        </Typography>

        <Typography variant="h5" component="h3">
          4. Privacy Acknowledgment
        </Typography>
        <Typography paragraph>
          By using TOG Services, you acknowledge your personal data will be
          handled in accordance with our Privacy Policy. TOG recommends all
          members read it.
        </Typography>

        <Typography variant="h5" component="h3">
          5. Content Ownership and Licensing
        </Typography>
        <Typography paragraph>
          You own any content (text, images, voice recordings, gameplay footage)
          you submit or share within TOG platforms. But by doing so you grant
          TOG a non-exclusive, royalty-free, worldwide license to use,
          reproduce, distribute and display such content for community and
          promotional purposes. Don't upload or share what you don't own.
        </Typography>

        <Typography variant="h5" component="h3">
          6. Changes to the Services
        </Typography>
        <Typography paragraph>
          TOG can change, suspend or terminate the Services at any time without
          notice. We are not responsible for any changes or termination.
        </Typography>

        <Typography variant="h5" component="h3">
          7. Account Suspension and Termination
        </Typography>
        <Typography paragraph>
          TOG can suspend or terminate your access to the Services for violating
          these Terms, the Code of Conduct or any behaviour deemed harmful to
          the community.
        </Typography>
        <Typography paragraph>
          Staff members must tell leadership if they intend to leave TOG.
        </Typography>
        <Typography paragraph>
          General members don't need to give notice but are encouraged to let us
          know especially if you plan to come back in the future.
        </Typography>

        <Typography variant="h5" component="h3">
          8. Limitation of Liability
        </Typography>
        <Typography paragraph>
          When you use our Services, TOG won't be held responsible for any of
          the following types of damages-you suffer emotionally, financially or
          in any other way. That includes distress caused by cyberbullying,
          technical failures or losing access to your account because of a
          third-party platform's issues.
        </Typography>

        <Typography variant="h5" component="h3">
          9. Indemnification
        </Typography>
        <Typography paragraph>
          You agree to protect and defend TOG, its leaders and affiliates from
          any claim, loss or expense that arises from your use of the Services.
          That includes if you break these Terms or applicable laws-and
          third-party rights.
        </Typography>

        <Typography variant="h5" component="h3">
          10. Jurisdictional Awareness
        </Typography>
        <Typography paragraph>
          As a user, you're responsible for making sure your participation in
          TOG complies with the laws and regulations of your own jurisdiction.
          That's your responsibility.
        </Typography>

        <Typography variant="h5" component="h3">
          11. Dispute Resolution
        </Typography>
        <Typography paragraph>
          You agree to try to resolve any disputes through TOG leadership first.
          If not resolved, disputes will be governed by the laws of the state of
          Louisiana, USA unless EU law requires otherwise for EU residents.
        </Typography>
        <Typography paragraph>
          Alternative dispute resolution methods may be available by mutual
          agreement.
        </Typography>

        <Typography variant="h5" component="h3">
          12. Changes to Terms
        </Typography>
        <Typography paragraph>
          TOG can change these Terms at any time. Continued use of the Services
          is acceptance of the new Terms. Notice of material changes will be
          given through official channels.
        </Typography>

        <Typography variant="h5" component="h3">
          Contact
        </Typography>
        <Typography paragraph>
          For questions about these Terms, data use or your rights:
          <br />
          <ObfuscatedEmail />
          <br />
          https://togx.org
        </Typography>
      </Box>
      <Notification />
    </Box>
  );
};

// Notification Component
const Notification = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const notificationRef = useRef(null);

  useEffect(() => {
    // Subscribe to custom event for notifications
    const handleNotification = (event) => {
      setMessage(event.detail.message);
      setOpen(true);

      // Auto-hide after 5 seconds
      setTimeout(() => {
        setOpen(false);
      }, 5000);
    };

    window.addEventListener("showNotification", handleNotification);

    return () => {
      window.removeEventListener("showNotification", handleNotification);
    };
  }, []);

  // Function to show notification from other components
  useEffect(() => {
    // Expose a global function to show notifications
    window.showNotification = (message) => {
      const event = new CustomEvent("showNotification", {
        detail: { message },
      });
      window.dispatchEvent(event);
    };

    return () => {
      window.showNotification = undefined;
    };
  }, []);

  if (!open) return null;

  return (
    <Box
      ref={notificationRef}
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 300,
        backgroundColor: "#1f1f1f",
        color: "white",
        borderRadius: 1,
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        zIndex: 9999,
        overflow: "hidden",
        animation: "slideIn 0.3s ease-out",
        "@keyframes slideIn": {
          from: { transform: "translateX(100%)" },
          to: { transform: "translateX(0)" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1,
          backgroundColor: "#0078d7",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2">TOG Notification</Typography>
        <IconButton
          size="small"
          sx={{ color: "white" }}
          onClick={() => setOpen(false)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">{message}</Typography>
      </Box>
    </Box>
  );
};

// Main Terms and Policies Component with Routing
const TermsAndPolicies = () => {
  const location = useLocation();
  const isIndex =
    location.pathname === "/policies" || location.pathname === "/policies/";

  const sections = [
    {
      id: "privacy",
      title: "Privacy policy",
      path: "/policies/privacy",
    },
    {
      id: "cookies",
      title: "Cookie Policy",
      path: "/policies/cookies",
    },
    {
      id: "terms",
      title: "Terms of Service",
      path: "/policies/terms",
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {isIndex && (
        <Box
          sx={{
            p: 4,
            mb: 4,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            align="center"
            sx={{
              color: "white",
              fontWeight: "bold",
              mb: 4,
            }}
          >
            TERMS AND POLICIES
          </Typography>

          <List sx={{ width: "100%" }}>
            {sections.map((section) => (
              <ListItem
                key={section.id}
                button
                component={Link}
                to={section.path}
                sx={{
                  border: "1px solid rgba(255, 255, 255, 0.555)",
                  borderRadius: 2,
                  py: 2,
                  backgroundColor: "transparent",
                  mb: 2, // Add margin bottom for spacing between items
                }}
              >
                <ListItemText
                  primary={section.title}
                  sx={{
                    color: "white",
                    "& .MuiTypography-root": {
                      fontSize: "1.25rem",
                      fontWeight: 500,
                    },
                  }}
                />
                <ListItemIcon sx={{ minWidth: "auto", color: "white" }}>
                  <ArrowForwardIosIcon />
                </ListItemIcon>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Box
        sx={{
          borderRadius: 2,
          p: 4,
          display: isIndex ? "none" : "block",
        }}
      >
        <Routes>
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="cookies" element={<CookiePolicy />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route path="/" element={<></>} />
          <Route path="*" element={<Navigate to="/policies" replace />} />
        </Routes>
      </Box>
    </Container>
  );
};

export default TermsAndPolicies;
